
import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const MethodologySection: React.FC = () => {
  const { t } = useLanguage();

  // Define fallback components in case translations aren't available
  const defaultComponents = [
    "Brand Equity Assessment",
    "Emotional Engagement Analysis",
    "Stakeholder Alignment Index",
    "Market Impact Potential"
  ];

  return (
    <div className="space-y-6">
      <Card className="border-gray-200 shadow-md">
        <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
          <CardTitle className="text-xl font-semibold text-gray-800">
            {t('methodology.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 pt-6">
          <div className="space-y-4">
            <h3 className="text-xl font-semibold mb-2 text-gray-800">{t('methodology.ea.title')}</h3>
            <p className="text-gray-600 text-sm">
              {t('methodology.ea.description')}
            </p>
            <ul className="list-disc pl-5 space-y-2 text-gray-500 text-xs">
              <li>{t('methodology.ea.metric1')}</li>
              <li>{t('methodology.ea.metric2')}</li>
              <li>{t('methodology.ea.metric3')}</li>
              <li>{t('methodology.ea.metric4')}</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold mb-2 text-gray-800">{t('methodology.elv.title')}</h3>
            <p className="text-gray-600 text-sm">
              {t('methodology.elv.description')}
            </p>
            <ul className="list-disc pl-5 space-y-2 text-gray-500 text-xs">
              <li>{t('methodology.elv.metric1')}</li>
              <li>{t('methodology.elv.metric2')}</li>
              <li>{t('methodology.elv.metric3')}</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold mb-2 text-gray-800">{t('methodology.l.title')}</h3>
            <p className="text-gray-600 text-sm">
              {t('methodology.l.description')}
            </p>
            <ul className="list-disc pl-5 space-y-2 text-gray-500 text-xs">
              <li>{t('methodology.l.metric1')}</li>
              <li>{t('methodology.l.metric2')}</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold mb-2 text-gray-800">Net Emotional Value (NEV)</h3>
            <p className="text-gray-600 text-sm">
              {t('methodology.nev.description')}
            </p>
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200 my-3">
              <p className="font-medium text-gray-700">{t('methodology.nev.formula')}</p>
              <p className="text-gray-500 mt-2 text-xs">{t('methodology.nev.explanation')}</p>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold mb-2 text-gray-800">{t('methodology.eroi.title')}</h3>
            <p className="text-gray-600 text-sm">
              {t('methodology.eroi.description')}
            </p>
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200 my-3">
              <p className="font-medium text-gray-700">{t('methodology.eroi.formula')}</p>
              <p className="font-bold text-gray-600 text-center my-2">{t('methodology.eroi.formula_text')}</p>
              <ul className="list-disc pl-5 space-y-2 text-gray-500 mt-3 text-xs">
                <li>{t('methodology.eroi.component1')}</li>
                <li>{t('methodology.eroi.component2')}</li>
                <li>{t('methodology.eroi.component3')}</li>
              </ul>
            </div>
            <div className="mt-4">
              <p className="font-medium text-gray-700">{t('methodology.eroi.interpretation')}</p>
              <ul className="list-disc pl-5 space-y-2 text-gray-500 mt-2 text-xs">
                <li>{t('methodology.eroi.interpretation1')}</li>
                <li>{t('methodology.eroi.interpretation2')}</li>
                <li>{t('methodology.eroi.interpretation3')}</li>
              </ul>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold mb-2 text-gray-800">{t('methodology.differentiators.title')}</h3>
            <p className="text-gray-600 text-sm">
              {t('methodology.differentiators.description')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-md border border-gray-200">
                <h4 className="font-semibold text-gray-700">{t('methodology.differentiators.factor1.title')}</h4>
                <p className="text-gray-500 mt-1 text-xs">{t('methodology.differentiators.factor1.description')}</p>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-md border border-gray-200">
                <h4 className="font-semibold text-gray-700">{t('methodology.differentiators.factor2.title')}</h4>
                <p className="text-gray-500 mt-1 text-xs">{t('methodology.differentiators.factor2.description')}</p>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-md border border-gray-200">
                <h4 className="font-semibold text-gray-700">{t('methodology.differentiators.factor3.title')}</h4>
                <p className="text-gray-500 mt-1 text-xs">{t('methodology.differentiators.factor3.description')}</p>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-md border border-gray-200">
                <h4 className="font-semibold text-gray-700">{t('methodology.differentiators.factor4.title')}</h4>
                <p className="text-gray-500 mt-1 text-xs">{t('methodology.differentiators.factor4.description')}</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold mb-2 text-gray-800">BEAM Score</h3>
            <p className="text-gray-600 text-sm">
              {t('methodology.beamScore.overview')}
            </p>
            <ul className="list-disc pl-5 space-y-2 text-gray-500 text-xs">
              {(() => {
                // Safely handle the BEAM score components
                try {
                  const components = t('methodology.beamScore.components');
                  // Check if components is an array before mapping
                  if (Array.isArray(components)) {
                    return components.map((component, index) => (
                      <li key={index}>{component}</li>
                    ));
                  } else {
                    // Fallback to default components if not an array
                    return defaultComponents.map((component, index) => (
                      <li key={index}>{component}</li>
                    ));
                  }
                } catch (error) {
                  // Additional safety in case of any errors
                  console.error("Error rendering BEAM score components", error);
                  return defaultComponents.map((component, index) => (
                    <li key={index}>{component}</li>
                  ));
                }
              })()}
            </ul>
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200 my-3">
              <p className="font-medium text-gray-700 mb-2">Interpretation Scale:</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <p className="text-gray-500 text-xs">{t('methodology.beamScore.interpretationScale.low').replace("0-30", "0-30")}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <p className="text-gray-500 text-xs">{t('methodology.beamScore.interpretationScale.medium').replace("4-7", "40-70")}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <p className="text-gray-500 text-xs">{t('methodology.beamScore.interpretationScale.high').replace("8-10", "80-100")}</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MethodologySection;
